import React, { useState, useEffect } from "react";

export type OutputItem = {
  type: "log" | "error" | "result";
  content: string;
};

interface UnifiedConsoleExternalResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  status: "pass" | "fail" | "error";
}

interface UnifiedConsoleProps {
  output: OutputItem[];
  // Deprecated/ignored props kept for backward-compatibility
  testCases?: Array<{ input: string; expectedOutput: string; description?: string }>;
  code?: string;
  language?: string;
  functionName?: string;
  setTestResults?: (testResults: any[]) => void;
  activeTab?: "console" | "testcase";
  onTabChange?: (tab: "console" | "testcase") => void;
  testsExecuted?: boolean;
  // New source of truth for test display
  externalResults?: UnifiedConsoleExternalResult[];
  isLoading?: boolean;
}

const UnifiedConsole: React.FC<UnifiedConsoleProps> = ({
  output,
  activeTab = "console",
  onTabChange,
  externalResults = [],
  testsExecuted = false,
  isLoading = false,
}) => {
  const [currentTab, setCurrentTab] = useState<"console" | "testcase">(
    activeTab
  );
  const [selectedTestCase, setSelectedTestCase] = useState<number>(0);

  useEffect(() => {
    setCurrentTab(activeTab);
  }, [activeTab]);

  const handleTabChange = (tab: "console" | "testcase") => {
    setCurrentTab(tab);
    onTabChange?.(tab);
  };

  

  const hasResults = externalResults && externalResults.length > 0;
  const passedTests = externalResults.filter((r) => r.status === "pass").length;

  const LoadingSkeleton = (
    <div className="h-full w-full bg-[#1e1e1e] text-white p-4">
      <div className="space-y-3 animate-pulse">
        <div className="h-4 bg-[#2b2b2b] rounded w-2/3"></div>
        <div className="h-4 bg-[#2b2b2b] rounded w-1/2"></div>
        <div className="h-4 bg-[#2b2b2b] rounded w-3/4"></div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col rounded-lg overflow-hidden bg-[#1e1e1e]">
      {/* Main Tabs */}
      <div className="bg-[#000000] flex  px-3 py-2">
        <button
          onClick={() => handleTabChange("console")}
          className={`px-3 py-1 text-sm font-medium transition-colors ${
            currentTab === "console"
              ? "text-white bg-transparent"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Console
        </button>
        <button
          onClick={() => (hasResults || testsExecuted) && handleTabChange("testcase")}
          disabled={!hasResults && !testsExecuted}
          className={`px-3 py-1 text-sm font-medium transition-colors ${
            currentTab === "testcase" && (hasResults || testsExecuted)
              ? "text-white bg-transparent"
              : (hasResults || testsExecuted)
              ? "text-gray-400 hover:text-white"
              : "text-gray-600 cursor-not-allowed opacity-50"
          }`}
        >
          Test case
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {currentTab === "console" ? (
          isLoading ? (
            LoadingSkeleton
          ) : (
            <div className="h-full w-full bg-[#1e1e1e] text-white overflow-y-auto minimal-scrollbar p-4">
              <div className="font-mono text-sm">
                {output.length === 0 ? (
                  <span className="text-gray-400">// Output will appear here...</span>
                ) : (
                  output.map((item, index) => (
                    <div
                      key={index}
                      className={`mb-1 ${
                        item.type === "error"
                          ? "text-red-400"
                          : item.type === "result"
                          ? "text-white"
                          : "text-white"
                      }`}
                    >
                      {item.type === "log" && "> "}
                      {item.type === "error" && "✖ "}
                      {item.type === "result" && "← "}
                      {item.content}
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        ) : (
          <div className="h-full flex flex-col bg-[#1e1e1e] overflow-hidden">
            {/* Test Case Tabs */}
            <div className="px-3 py-2 shrink">
              <div className="flex gap-4 items-center">
                <div className="flex gap-2">
                  {(isLoading ? new Array(3).fill(null) : externalResults).map((result, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedTestCase(index)}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        selectedTestCase === index
                          ? "text-white bg-[#3d3d3d]"
                          : "text-gray-400 hover:text-white hover:bg-[#333333]"
                      }`}
                      >
                      {isLoading ? (
                        <div className="w-2 h-2 rounded-full bg-[#2b2b2b]"></div>
                      ) : (
                        <div
                          className={`w-2 h-2 rounded-full ${
                            result && (result as any).status === "pass" ? "bg-green-500" : "bg-red-500"
                          }`}
                        ></div>
                      )}
                      Case {index + 1}
                    </button>
                  ))}
                </div>
                <div className="ml-auto mr-3">
                  {isLoading ? (
                    <div className="h-4 w-32 bg-[#2b2b2b] rounded animate-pulse" />
                  ) : (
                    <span
                      className={`text-lg font-medium ${
                        passedTests === externalResults.length
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {passedTests === externalResults.length
                        ? "Correct Answer"
                        : "Wrong Answer"}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Test Case Content */}
            <div className="flex-1 bg-[#1e1e1e] text-white overflow-y-auto minimal-scrollbar">
              {isLoading ? (
                <div className="p-6 space-y-4 animate-pulse">
                  <div className="bg-[#FFFFFF1A] px-2 py-3 rounded-lg">
                    <div className="h-4 bg-[#2b2b2b] rounded w-3/4" />
                  </div>
                  <div className="bg-[#FFFFFF1A] px-2 py-3 rounded-lg">
                    <div className="h-4 bg-[#2b2b2b] rounded w-2/3" />
                  </div>
                  <div className="bg-[#FFFFFF1A] px-2 py-3 rounded-lg">
                    <div className="h-4 bg-[#2b2b2b] rounded w-1/2" />
                  </div>
                </div>
              ) : hasResults && externalResults[selectedTestCase] ? (
                <div className="p-6 space-y-4">
                  <div className="bg-[#FFFFFF1A] px-2 py-2.5 rounded-lg">
                    <div className="text-white">
                      <span className="text-gray-400">Input = </span>
                      <span>{externalResults[selectedTestCase].input}</span>
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF1A] px-2 py-2.5 rounded-lg">
                    <div className="text-white">
                      <span className="text-gray-400">Expected = </span>
                      <span>{externalResults[selectedTestCase].expectedOutput}</span>
                    </div>
                  </div>
                  <div className="bg-[#FFFFFF1A] px-2 py-2.5 rounded-lg">
                    <div className="text-white">
                      <span className="text-gray-400">Actual = </span>
                      <span>{externalResults[selectedTestCase].actualOutput}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-gray-400">No test results available</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedConsole;
